import React, { useMemo, useState } from 'react';
import TeamBadge, { pickTeamLogo } from './TeamBadge';
import FormPills from './FormPills';
import { goalsDisplay, normalizeTableRow, rowHasHomeAway } from './leagueTableUtils';
import { useWidgetLabels } from './WidgetLabelsContext';

const ZONE_BAR = {
  ucl: '#1e5aa8',
  uel: '#9b2743',
  uecl: '#c9a227',
  promotion: '#00a839',
  playoff: '#7c5cbf',
  relegation: '#e40000',
};

const LEGEND_ITEMS = [
  { zone: 'ucl', label: 'Promotion — Champions League (League phase)' },
  { zone: 'uel', label: 'Promotion — Europa League (League phase)' },
  { zone: 'uecl', label: 'Promotion — Conference League (Qualification)' },
  { zone: 'relegation', label: 'Relegation' },
];

function ViewTabs({ view, onChange, labels }) {
  const tabs = [
    { id: 'overall', label: labels.overall || labels.all || 'OVERALL' },
    { id: 'home', label: labels.home || 'HOME' },
    { id: 'away', label: labels.away || 'AWAY' },
  ];

  return (
    <div className="flex gap-1 mb-3">
      {tabs.map((tab) => {
        const active = view === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide rounded-md transition-colors"
            style={{
              background: active ? 'var(--sw-table-tab-active)' : 'transparent',
              color: active ? 'var(--sw-text)' : 'var(--sw-muted)',
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function RankCell({ position, zone }) {
  const bar = zone ? ZONE_BAR[zone] : null;
  return (
    <td className="lt-rank relative w-9 text-center font-semibold tabular-nums text-sm">
      {bar && (
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ backgroundColor: bar }}
          aria-hidden
        />
      )}
      <span className="relative z-[1] pl-1">{position}</span>
    </td>
  );
}

function Legend() {
  const items = LEGEND_ITEMS.filter((item) => ZONE_BAR[item.zone]);
  return (
    <div className="mt-4 pt-3 border-t space-y-2" style={{ borderColor: 'var(--sw-table-border)' }}>
      {items.map((item) => (
        <div key={item.zone} className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--sw-muted)' }}>
          <span
            className="w-3 h-3 shrink-0"
            style={{ backgroundColor: ZONE_BAR[item.zone] }}
            aria-hidden
          />
          <span>{item.label}</span>
        </div>
      ))}
      <p className="text-[10px] pt-1 leading-snug" style={{ color: 'var(--sw-muted)' }}>
        If teams finish on equal points at the end of the season, score difference will be the
        tie-breaker.
      </p>
    </div>
  );
}

export default function LeagueTable({
  rows = [],
  showTabs = true,
  showLegend = true,
  showForm = true,
  emptyMessage = 'No data available.',
}) {
  const t = useWidgetLabels();
  const hasHomeAway = rowHasHomeAway(rows);
  const [view, setView] = useState('overall');

  const normalized = useMemo(
    () => rows.map((row) => normalizeTableRow(row, showTabs && hasHomeAway ? view : 'overall')),
    [rows, view, showTabs, hasHomeAway]
  );

  const hasStats = normalized.some((r) => r.played != null && r.played !== '');

  if (!normalized.length) {
    return (
      <p className="text-center text-xs py-8" style={{ color: 'var(--sw-muted)' }}>
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="sw-league-table">
      {showTabs && hasHomeAway && (
        <ViewTabs
          view={view}
          onChange={setView}
          labels={{ overall: t('overall'), all: t('all'), home: t('home'), away: t('away') }}
        />
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr
              className="text-left text-[10px] font-bold uppercase tracking-wider"
              style={{ color: 'var(--sw-muted)', borderBottom: '1px solid var(--sw-table-border)' }}
            >
              <th className="py-2 pr-1 w-9 text-center">#</th>
              <th className="py-2 pl-1 min-w-[12rem]">{t('team')}</th>
              {hasStats && (
                <>
                  <th className="py-2 px-1 text-center w-8">MP</th>
                  <th className="py-2 px-1 text-center w-7">W</th>
                  <th className="py-2 px-1 text-center w-7">D</th>
                  <th className="py-2 px-1 text-center w-7">L</th>
                  <th className="py-2 px-1 text-center w-11">G</th>
                  <th className="py-2 px-1 text-center w-8">GD</th>
                </>
              )}
              <th className="py-2 px-1 text-center w-9 font-bold">PTS</th>
              {showForm && hasStats && (
                <th className="py-2 pl-2 text-right min-w-[6rem]">{t('form')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {normalized.map((r, i) => (
              <tr
                key={`${r.position}-${r.team}-${i}`}
                style={{
                  background: i % 2 === 0 ? 'var(--sw-table-row)' : 'var(--sw-table-row-alt)',
                }}
              >
                <RankCell position={r.position} zone={r.zone} />
                <td className="py-2.5 pr-2">
                  <span className="flex items-center gap-2 min-w-0">
                    <TeamBadge name={r.team} logoUrl={pickTeamLogo(r)} size={20} />
                    <span className="font-medium leading-snug">{r.team}</span>
                  </span>
                </td>
                {hasStats && (
                  <>
                    <td className="py-2.5 text-center tabular-nums text-xs">{r.played ?? '—'}</td>
                    <td className="py-2.5 text-center tabular-nums text-xs">{r.won ?? '—'}</td>
                    <td className="py-2.5 text-center tabular-nums text-xs">{r.drawn ?? '—'}</td>
                    <td className="py-2.5 text-center tabular-nums text-xs">{r.lost ?? '—'}</td>
                    <td className="py-2.5 text-center tabular-nums text-xs whitespace-nowrap">
                      {goalsDisplay(r.gf, r.ga)}
                    </td>
                    <td className="py-2.5 text-center tabular-nums text-xs">{r.gd ?? '—'}</td>
                  </>
                )}
                <td className="py-2.5 text-center font-bold tabular-nums">{r.points ?? '—'}</td>
                {showForm && hasStats && (
                  <td className="py-2.5 pr-1 text-right">
                    <FormPills form={r.form} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showLegend && <Legend />}
    </div>
  );
}
